/**
 * The local mirror of the event log: IndexedDB, wrapped so that no caller ever
 * has to think about storage being unavailable.
 *
 * Everything here is a cache in the strict sense. The server's event log is the
 * source of truth; this exists so a cold start can paint the last known state
 * before the network answers, and so an optimistic message survives a reload
 * with its `client_message_id` intact. Losing the whole database costs one
 * bootstrap `/sync`, never a message.
 *
 * That is why every operation degrades instead of failing. Private browsing,
 * "block all cookies", a wedged database and an exhausted quota all present as
 * a thrown request somewhere in here, and in each case the right answer is to
 * carry on in memory rather than to take the application down with it.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

import type { Attachment, Cursor, Message, Room, UUID } from '@/api/types'

const DB_NAME = 'relaii'
const DB_VERSION = 1

/**
 * Safari has been known to leave `indexedDB.open` pending forever after a crash
 * or when storage is being evicted. First paint reads the cache, so a hang here
 * is a hang of the whole application; time it out and fall back to memory.
 */
const OPEN_TIMEOUT_MS = 5_000

/** Retained history per room. Older messages are always refetchable by `before`. */
export const CACHED_MESSAGES_PER_ROOM = 500

const META_CURSOR = 'cursor'
const META_ROOM_SYNC_VERSION = 'room_sync_version'
const ROOM_SYNC_VERSION = 1
const META_USER_ID = 'user_id'
const META_SERVER_ID = 'server_id'

const STORE_NAMES = ['meta', 'rooms', 'messages', 'outbox'] as const

// ── Records ─────────────────────────────────────────────────────────────────

/**
 * A message the user has composed that the server has not yet acknowledged.
 *
 * `client_message_id` is the primary key because it is the identity the server
 * shares with us: re-posting one returns the original message rather than a
 * duplicate, which is what makes retry-on-reload safe.
 */
export interface PendingMessage {
  client_message_id: string
  room_id: UUID
  text: string
  attachment_ids: UUID[]
  /** Kept beside the ids so a retry after a reload can still render the files. */
  attachments: Attachment[]
  /** Root of the thread this send belongs to, when it is a reply. */
  parent_id?: UUID | null
  created_at: string
  status: 'sending' | 'failed'
  error: string | null
  attempts: number
  /**
   * False once the server has refused the message rather than failed to
   * receive it. Such a row is kept — the text is the user's — but never posted
   * again on its own, because the refusal would only be repeated.
   *
   * Optional because rows written before the field existed have no value for
   * it, and the honest reading of "unknown" here is "worth trying once more".
   */
  retryable?: boolean
}

/** Whose data the cache holds. A mismatch means the cache must be discarded. */
export interface CachedIdentity {
  user_id: UUID | null
  server_id: string | null
}

export interface MessageQuery {
  /** Exclusive upper bound on `room_sequence`, matching `GET …/messages?before=`. */
  before?: number
  limit?: number
}

type MetaValue = string | number | null

interface RelaiiSchema extends DBSchema {
  meta: { key: string; value: MetaValue }
  rooms: { key: string; value: Room }
  messages: {
    key: [string, number]
    value: Message
    indexes: { by_room: string }
  }
  outbox: {
    key: string
    value: PendingMessage
    indexes: { by_room: string }
  }
}

// ── The backend contract ────────────────────────────────────────────────────

interface CacheBackend {
  /** False for the in-memory fallback, so callers can warn about persistence. */
  readonly persistent: boolean
  loadCursor(): Promise<Cursor>
  saveCursor(cursor: Cursor): Promise<void>
  loadIdentity(): Promise<CachedIdentity>
  saveIdentity(identity: CachedIdentity): Promise<void>
  loadRooms(): Promise<Room[]>
  saveRooms(rooms: readonly Room[]): Promise<void>
  replaceRooms(rooms: readonly Room[]): Promise<void>
  removeRoom(roomId: UUID): Promise<void>
  loadMessages(roomId: UUID, query: MessageQuery): Promise<Message[]>
  saveMessages(messages: readonly Message[]): Promise<void>
  refreshMessageAvatar(userId: UUID, avatarUrl: string | null): Promise<void>
  loadOutbox(): Promise<PendingMessage[]>
  savePending(pending: PendingMessage): Promise<void>
  removePending(clientMessageId: string): Promise<void>
  clear(): Promise<void>
  /** Everything but the outbox. */
  clearLog(): Promise<void>
  close(): void
}

function sortBySequence(messages: Message[]): Message[] {
  return messages.sort((left, right) => left.room_sequence - right.room_sequence)
}

// ── In-memory fallback ──────────────────────────────────────────────────────

/**
 * Used when IndexedDB is absent, refused or broken. It keeps the same shape so
 * the sync engine has one code path, and it is bounded by the same per-room cap
 * so a long-lived tab cannot grow without limit.
 */
class MemoryBackend implements CacheBackend {
  readonly persistent = false

  private cursor: Cursor = 0
  private identity: CachedIdentity = { user_id: null, server_id: null }
  private readonly rooms = new Map<UUID, Room>()
  private readonly messages = new Map<UUID, Map<number, Message>>()
  private readonly outbox = new Map<string, PendingMessage>()

  loadCursor(): Promise<Cursor> {
    return Promise.resolve(this.cursor)
  }

  saveCursor(cursor: Cursor): Promise<void> {
    this.cursor = cursor
    return Promise.resolve()
  }

  loadIdentity(): Promise<CachedIdentity> {
    return Promise.resolve({ ...this.identity })
  }

  saveIdentity(identity: CachedIdentity): Promise<void> {
    this.identity = { ...identity }
    return Promise.resolve()
  }

  loadRooms(): Promise<Room[]> {
    return Promise.resolve([...this.rooms.values()])
  }

  saveRooms(rooms: readonly Room[]): Promise<void> {
    for (const room of rooms) this.rooms.set(room.id, room)
    return Promise.resolve()
  }

  replaceRooms(rooms: readonly Room[]): Promise<void> {
    this.rooms.clear()
    return this.saveRooms(rooms)
  }

  removeRoom(roomId: UUID): Promise<void> {
    this.rooms.delete(roomId)
    this.messages.delete(roomId)
    for (const [key, pending] of this.outbox) {
      if (pending.room_id === roomId) this.outbox.delete(key)
    }
    return Promise.resolve()
  }

  loadMessages(roomId: UUID, query: MessageQuery): Promise<Message[]> {
    const room = this.messages.get(roomId)
    if (room === undefined) return Promise.resolve([])

    const limit = query.limit ?? CACHED_MESSAGES_PER_ROOM
    const ceiling = query.before ?? Number.MAX_SAFE_INTEGER
    const matching = sortBySequence(
      [...room.values()].filter((message) => message.room_sequence < ceiling),
    )

    return Promise.resolve(matching.slice(Math.max(0, matching.length - limit)))
  }

  saveMessages(messages: readonly Message[]): Promise<void> {
    for (const message of messages) {
      let room = this.messages.get(message.room_id)
      if (room === undefined) {
        room = new Map<number, Message>()
        this.messages.set(message.room_id, room)
      }
      room.set(message.room_sequence, message)
    }

    for (const room of this.messages.values()) {
      const excess = room.size - CACHED_MESSAGES_PER_ROOM
      if (excess <= 0) continue

      const oldest = [...room.keys()].sort((left, right) => left - right).slice(0, excess)
      for (const sequence of oldest) room.delete(sequence)
    }

    return Promise.resolve()
  }

  refreshMessageAvatar(userId: UUID, avatarUrl: string | null): Promise<void> {
    for (const room of this.messages.values()) {
      for (const [sequence, message] of room) {
        if (message.sender.id !== userId || message.sender.avatar_url === avatarUrl) continue
        room.set(sequence, { ...message, sender: { ...message.sender, avatar_url: avatarUrl } })
      }
    }
    return Promise.resolve()
  }

  loadOutbox(): Promise<PendingMessage[]> {
    return Promise.resolve([...this.outbox.values()])
  }

  savePending(pending: PendingMessage): Promise<void> {
    this.outbox.set(pending.client_message_id, pending)
    return Promise.resolve()
  }

  removePending(clientMessageId: string): Promise<void> {
    this.outbox.delete(clientMessageId)
    return Promise.resolve()
  }

  clear(): Promise<void> {
    this.cursor = 0
    this.identity = { user_id: null, server_id: null }
    this.rooms.clear()
    this.messages.clear()
    this.outbox.clear()
    return Promise.resolve()
  }

  clearLog(): Promise<void> {
    this.cursor = 0
    this.identity = { user_id: null, server_id: null }
    this.rooms.clear()
    this.messages.clear()
    return Promise.resolve()
  }

  close(): void {
    // Nothing to release.
  }
}

// ── IndexedDB ───────────────────────────────────────────────────────────────

function roomRange(roomId: UUID, before?: number): IDBKeyRange {
  const upper = before === undefined ? Number.MAX_SAFE_INTEGER : before - 1
  return IDBKeyRange.bound([roomId, 0], [roomId, upper])
}

class IdbBackend implements CacheBackend {
  readonly persistent = true

  constructor(private readonly db: IDBPDatabase<RelaiiSchema>) {}

  async loadCursor(): Promise<Cursor> {
    const [value, version] = await Promise.all([
      this.db.get('meta', META_CURSOR),
      this.db.get('meta', META_ROOM_SYNC_VERSION),
    ])
    // Older clients advanced the cursor without persisting room changes from
    // sync events. Bootstrap once to repair every cached membership, retaining
    // the account, history and unsent messages in the meantime.
    if (version !== ROOM_SYNC_VERSION) return 0
    return typeof value === 'number' && Number.isFinite(value) ? value : 0
  }

  async saveCursor(cursor: Cursor): Promise<void> {
    const tx = this.db.transaction('meta', 'readwrite')
    await tx.store.put(cursor, META_CURSOR)
    await tx.store.put(ROOM_SYNC_VERSION, META_ROOM_SYNC_VERSION)
    await tx.done
  }

  async loadIdentity(): Promise<CachedIdentity> {
    const [user, server] = await Promise.all([
      this.db.get('meta', META_USER_ID),
      this.db.get('meta', META_SERVER_ID),
    ])

    return {
      user_id: typeof user === 'string' ? user : null,
      server_id: typeof server === 'string' ? server : null,
    }
  }

  async saveIdentity(identity: CachedIdentity): Promise<void> {
    const tx = this.db.transaction('meta', 'readwrite')
    await Promise.all([
      tx.store.put(identity.user_id, META_USER_ID),
      tx.store.put(identity.server_id, META_SERVER_ID),
      tx.done,
    ])
  }

  loadRooms(): Promise<Room[]> {
    return this.db.getAll('rooms')
  }

  async saveRooms(rooms: readonly Room[]): Promise<void> {
    if (rooms.length === 0) return

    const tx = this.db.transaction('rooms', 'readwrite')
    await Promise.all([...rooms.map((room) => tx.store.put(room)), tx.done])
  }

  async replaceRooms(rooms: readonly Room[]): Promise<void> {
    const tx = this.db.transaction('rooms', 'readwrite')
    await tx.store.clear()
    await Promise.all([...rooms.map((room) => tx.store.put(room)), tx.done])
  }

  async removeRoom(roomId: UUID): Promise<void> {
    const tx = this.db.transaction(STORE_NAMES, 'readwrite')

    await tx.objectStore('rooms').delete(roomId)
    await tx.objectStore('messages').delete(roomRange(roomId))

    const outbox = tx.objectStore('outbox').index('by_room')
    for (const key of await outbox.getAllKeys(roomId)) {
      await tx.objectStore('outbox').delete(key)
    }

    await tx.done
  }

  async loadMessages(roomId: UUID, query: MessageQuery): Promise<Message[]> {
    const limit = query.limit ?? CACHED_MESSAGES_PER_ROOM
    if (limit <= 0) return []

    const found: Message[] = []
    const tx = this.db.transaction('messages')

    // Walked backwards from the newest so a page costs `limit` reads rather
    // than a full scan of the room.
    let cursor = await tx.store.openCursor(roomRange(roomId, query.before), 'prev')
    while (cursor !== null && found.length < limit) {
      found.push(cursor.value)
      cursor = await cursor.continue()
    }

    await tx.done
    return found.reverse()
  }

  async saveMessages(messages: readonly Message[]): Promise<void> {
    if (messages.length === 0) return

    const tx = this.db.transaction('messages', 'readwrite')
    const touched = new Set<UUID>()

    for (const message of messages) {
      touched.add(message.room_id)
      await tx.store.put(message)
    }

    for (const roomId of touched) {
      const excess = (await tx.store.index('by_room').count(roomId)) - CACHED_MESSAGES_PER_ROOM
      if (excess <= 0) continue

      // Trimming the oldest keeps the retained block contiguous with the newest
      // message, which is what the store's gap detection relies on.
      let cursor = await tx.store.openCursor(roomRange(roomId))
      let removed = 0
      while (cursor !== null && removed < excess) {
        await cursor.delete()
        removed += 1
        cursor = await cursor.continue()
      }
    }

    await tx.done
  }

  async refreshMessageAvatar(userId: UUID, avatarUrl: string | null): Promise<void> {
    const tx = this.db.transaction('messages', 'readwrite')
    let cursor = await tx.store.openCursor()

    while (cursor !== null) {
      const message = cursor.value
      if (message.sender.id === userId && message.sender.avatar_url !== avatarUrl) {
        await cursor.update({ ...message, sender: { ...message.sender, avatar_url: avatarUrl } })
      }
      cursor = await cursor.continue()
    }

    await tx.done
  }

  loadOutbox(): Promise<PendingMessage[]> {
    return this.db.getAll('outbox')
  }

  async savePending(pending: PendingMessage): Promise<void> {
    await this.db.put('outbox', pending)
  }

  async removePending(clientMessageId: string): Promise<void> {
    await this.db.delete('outbox', clientMessageId)
  }

  async clear(): Promise<void> {
    const tx = this.db.transaction(STORE_NAMES, 'readwrite')
    await Promise.all([
      tx.objectStore('meta').clear(),
      tx.objectStore('rooms').clear(),
      tx.objectStore('messages').clear(),
      tx.objectStore('outbox').clear(),
      tx.done,
    ])
  }

  async clearLog(): Promise<void> {
    const tx = this.db.transaction(['meta', 'rooms', 'messages'], 'readwrite')
    await Promise.all([
      tx.objectStore('meta').clear(),
      tx.objectStore('rooms').clear(),
      tx.objectStore('messages').clear(),
      tx.done,
    ])
  }

  close(): void {
    this.db.close()
  }
}

// ── Opening ─────────────────────────────────────────────────────────────────

let warned = false

function warnOnce(reason: unknown): void {
  if (warned) return
  warned = true
  // One line, once, to explain why a reload will be slower than usual. Not an
  // error: the client is fully correct without a cache.
  console.warn('relaii: local cache unavailable, continuing in memory.', reason)
}

function openDatabase(): Promise<IDBPDatabase<RelaiiSchema>> {
  let handle: IDBPDatabase<RelaiiSchema> | null = null

  const opening = openDB<RelaiiSchema>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      database.createObjectStore('meta')
      database.createObjectStore('rooms', { keyPath: 'id' })

      const messages = database.createObjectStore('messages', {
        keyPath: ['room_id', 'room_sequence'],
      })
      messages.createIndex('by_room', 'room_id')

      const outbox = database.createObjectStore('outbox', { keyPath: 'client_message_id' })
      outbox.createIndex('by_room', 'room_id')
    },
    blocking() {
      // Another tab is upgrading. Holding the old version open would wedge it.
      handle?.close()
      backend = Promise.resolve(new MemoryBackend())
    },
    terminated() {
      backend = Promise.resolve(new MemoryBackend())
    },
  })

  return new Promise<IDBPDatabase<RelaiiSchema>>((resolve, reject) => {
    let settled = false

    const timer = setTimeout(() => {
      settled = true
      reject(new Error('IndexedDB did not open within the timeout.'))
    }, OPEN_TIMEOUT_MS)

    opening.then(
      (database) => {
        clearTimeout(timer)
        handle = database
        if (settled) database.close()
        else resolve(database)
      },
      (error: unknown) => {
        clearTimeout(timer)
        if (!settled) reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

async function createBackend(): Promise<CacheBackend> {
  if (typeof indexedDB === 'undefined') return new MemoryBackend()

  try {
    return new IdbBackend(await openDatabase())
  } catch (error) {
    warnOnce(error)
    return new MemoryBackend()
  }
}

let backend: Promise<CacheBackend> | null = null

function activeBackend(): Promise<CacheBackend> {
  backend ??= createBackend()
  return backend
}

/**
 * Runs one operation, demoting the whole cache to memory if storage throws.
 *
 * A failure here is nearly always the browser withdrawing storage mid-session —
 * quota exhausted, the user clearing site data, an eviction in private mode.
 * The application state lives in the store either way, so the correct response
 * is to stop persisting and keep going.
 */
async function run<T>(operation: (target: CacheBackend) => Promise<T>): Promise<T> {
  const target = await activeBackend()

  try {
    return await operation(target)
  } catch (error) {
    if (!target.persistent) throw error

    warnOnce(error)
    const memory = new MemoryBackend()
    backend = Promise.resolve(memory)
    try {
      target.close()
    } catch {
      // Already gone; nothing to do.
    }

    return operation(memory)
  }
}

// ── Public surface ──────────────────────────────────────────────────────────

export function loadCursor(): Promise<Cursor> {
  return run((target) => target.loadCursor())
}

export function saveCursor(cursor: Cursor): Promise<void> {
  return run((target) => target.saveCursor(cursor))
}

export function loadIdentity(): Promise<CachedIdentity> {
  return run((target) => target.loadIdentity())
}

export function saveIdentity(identity: CachedIdentity): Promise<void> {
  return run((target) => target.saveIdentity(identity))
}

export function loadRooms(): Promise<Room[]> {
  return run((target) => target.loadRooms())
}

export function saveRooms(rooms: readonly Room[]): Promise<void> {
  return run((target) => target.saveRooms(rooms))
}

/** Used after a full bootstrap, where rooms the user has left must disappear. */
export function replaceRooms(rooms: readonly Room[]): Promise<void> {
  return run((target) => target.replaceRooms(rooms))
}

export function removeRoom(roomId: UUID): Promise<void> {
  return run((target) => target.removeRoom(roomId))
}

export function loadMessages(roomId: UUID, query: MessageQuery = {}): Promise<Message[]> {
  return run((target) => target.loadMessages(roomId, query))
}

export function saveMessages(messages: readonly Message[]): Promise<void> {
  return run((target) => target.saveMessages(messages))
}

/** Updates every cached history entry for a person, including rooms not open in this tab. */
export function refreshMessageAvatar(userId: UUID, avatarUrl: string | null): Promise<void> {
  return run((target) => target.refreshMessageAvatar(userId, avatarUrl))
}

export function loadOutbox(): Promise<PendingMessage[]> {
  return run((target) => target.loadOutbox())
}

export function savePending(pending: PendingMessage): Promise<void> {
  return run((target) => target.savePending(pending))
}

export function removePending(clientMessageId: string): Promise<void> {
  return run((target) => target.removePending(clientMessageId))
}

/**
 * Discards everything. Called when a different user signs in, and on
 * sign-out: the cached log and the outbox belong to someone else.
 */
export function resetCache(): Promise<void> {
  return run((target) => target.clear())
}

/**
 * Discards the cached log and keeps the outbox. Called when `/sync` answers
 * `reset: true`: the history is too old to reconcile, but messages this
 * person wrote and the server never received are still theirs to send. The
 * identity goes with the rest of `meta`, so the caller writes it back before
 * the next cold start reads its absence as someone else's cache.
 */
export function resetLog(): Promise<void> {
  return run((target) => target.clearLog())
}

/** Whether writes are actually reaching disk. Diagnostics only. */
export async function isCachePersistent(): Promise<boolean> {
  return (await activeBackend()).persistent
}

export async function closeCache(): Promise<void> {
  if (backend === null) return

  const target = await backend
  backend = null
  try {
    target.close()
  } catch {
    // Already gone.
  }
}
